<template>
    <div class="phu-assign">
        <h2 class="phu-assign__title">{{ title }}</h2>

        <div class="phu-assign__body">
            <div class="phu-assign__tabs" role="tablist">
                <button
                    v-for="tab in workstations"
                    :key="tab.id"
                    type="button"
                    class="phu-assign__tab"
                    :class="{ 'phu-assign__tab--active': tab.id === activeWorkstationId }"
                    role="tab"
                    :aria-selected="tab.id === activeWorkstationId"
                    @click="selectWorkstation(tab.id)"
                >
                    {{ tab.name }}
                </button>
            </div>

            <p v-if="!currentPhu" class="phu-assign__empty">{{ $t('phu_production.assign.no_phus') }}</p>

            <div v-else class="phu-assign__carousel">
                <button type="button" class="phu-assign__arrow" :disabled="phuIndex === 0" :aria-label="$t('phu_production.assign.previous')" @click="stepPhu(-1)">
                    <i class="i-andy-chevron-left i-andy-s" aria-hidden="true"></i>
                </button>

                <article class="phu-assign__card">
                    <h3 class="phu-assign__name">{{ currentPhu.name }}</h3>
                    <PhuGrid :slots="currentPhu.slots" :columns="currentPhu.columns">
                        <template v-slot="{ item }">
                            <PhuSlot
                                :slot-item="item"
                                :product="slotProduct(item)"
                                :now="now"
                                :force-disabled="!isSameProduct(item)"
                                :selectable="isCompatible(item)"
                                :selected="isSelected(item)"
                                :selected-units="units"
                                @press="selectSlot(item)"
                            />
                        </template>
                    </PhuGrid>
                </article>

                <button type="button" class="phu-assign__arrow" :disabled="phuIndex >= phusOfWorkstation.length - 1" :aria-label="$t('phu_production.assign.next')" @click="stepPhu(1)">
                    <i class="i-andy-chevron-right i-andy-s" aria-hidden="true"></i>
                </button>
            </div>

            <p class="phu-assign__hint">
                <i class="i-andy-info-main i-andy-s" aria-hidden="true"></i>
                <span>{{ $t('phu_production.assign.hint') }}</span>
            </p>
        </div>
    </div>
</template>

<script>
import PhuGrid from '@/components/domain/phu-production/holding/PhuGrid.vue'
import PhuSlot from '@/components/domain/phu-production/holding/PhuSlot.vue'

export default {
    name: 'PopupPhuAssign',
    components: { PhuGrid, PhuSlot },
    props: {
        title: { type: String, default: '' },
        phus: { type: Array, default: () => [] },
        workstations: { type: Array, default: () => [] },
        products: { type: Object, default: () => ({}) },
        productId: { type: [String, Number], default: null },
        units: { type: Number, default: 0 },
        workstationId: { type: [String, Number], default: null },
        now: { type: Number, required: true },
        selectedId: { type: [String, Number], default: null }
    },
    data() {
        return {
            activeWorkstationId: this.workstationId,
            phuIndex: 0
        }
    },
    computed: {
        phusOfWorkstation() {
            return this.phus
                .filter((phu) => phu.workstation_id === this.activeWorkstationId)
                .slice()
                .sort((left, right) => left.sort_order - right.sort_order)
        },
        currentPhu() {
            return this.phusOfWorkstation[this.phuIndex] || null
        }
    },
    methods: {
        selectWorkstation(workstationId) {
            this.activeWorkstationId = workstationId
            this.phuIndex = 0
        },
        stepPhu(step) {
            const next = this.phuIndex + step
            if (next < 0 || next >= this.phusOfWorkstation.length) {
                return
            }
            this.phuIndex = next
        },
        isSameProduct(slot) {
            return String(slot.product_id) === String(this.productId)
        },
        isCompatible(slot) {
            return this.isSameProduct(slot) && !slot.batch_id && !Number(slot.units)
        },
        isSelected(slot) {
            return String(this.selectedId) === String(slot.id)
        },
        slotProduct(slot) {
            if (!this.isSameProduct(slot)) {
                return null
            }
            return this.products[slot.product_id] || null
        },
        selectSlot(slot) {
            if (!this.isCompatible(slot)) {
                return
            }
            this.$emit('select', slot.id)
        }
    }
}
</script>

<style lang="scss" scoped>
@import '~@/components/domain/phu-production/styles/tokens';

.phu-assign {
    @include display-flex();
    @include flex-direction(column);
    @include align-items(stretch);
    width: 100%;
    gap: 16px;

    &__title {
        margin: 0;
        font-family: $text-bold;
        @include font-size(m);
        color: $neutro-s80;
    }

    &__body {
        @include display-flex();
        @include flex-direction(column);
        gap: 16px;
        width: 100%;
        padding: 16px 22px;
        border-radius: $phu-radius-s;
        background-color: $neutro-t90;
    }

    &__tabs {
        @include display-flex();
        gap: $phu-gap;
        width: fit-content;
        padding: $phu-gap;
        border: 1px solid $neutro-t30;
        border-radius: $phu-radius-s;
        background-color: $phu-surface;
    }

    &__tab {
        min-height: 36px;
        padding: $phu-gap;
        border: none;
        border-radius: $phu-radius-s;
        background: transparent;
        font-family: $text;
        @include font-size(sm);
        color: $neutro-s50;
        cursor: pointer;

        &--active {
            background-color: $main-t90;
            font-family: $text-bold;
            color: $main-dark;
        }
    }

    &__carousel {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        gap: 16px;
        width: 100%;
    }

    &__arrow {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        flex: 0 0 auto;
        width: 36px;
        height: 36px;
        border: none;
        border-radius: $phu-radius-s;
        background-color: $main-t90;
        color: $main-dark;
        cursor: pointer;

        &:disabled {
            opacity: 0.4;
            cursor: default;
        }
    }

    &__card {
        flex: 1 1 auto;
        min-width: 0;
        max-width: 512px;
        border: 1px solid $neutro-dark;
        border-radius: $phu-radius-s;
        background-color: $phu-surface;
        box-shadow: 0 0 4px rgba(0, 0, 0, 0.04), 0 4px 8px rgba(0, 0, 0, 0.06);
    }

    &__name {
        margin: 0;
        padding: 8px 8px 4px;
        border-radius: $phu-radius-s;
        background-color: $neutro-t80;
        font-family: $text-bold;
        @include font-size(sm);
        line-height: 18px;
        color: $neutro-s80;
    }

    &__hint {
        @include display-flex();
        @include align-items(center);
        gap: $phu-gap;
        width: fit-content;
        margin: 0;
        padding: 8px 16px;
        border-radius: $phu-radius-s;
        background-color: $info-light;
        font-family: $text;
        @include font-size(sm);
        color: $neutro-s80;
    }

    &__empty {
        margin: 0;
        font-family: $text;
        @include font-size(sm);
        color: $neutro-s50;
    }
}
</style>
